import React, { useMemo, useState } from "react";
import {
  analyticsEventTypes,
  getValueFromParentData,
  parseCssStringToObject,
  pickRandomValueFromArray,
} from "../../../../helper";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import { useAction } from "../../../../hooks/useAction";
import { useFormContext } from "react-hook-form";
import { useFormSumbmit } from "../../../../hooks/useFormSubmit";
import { useAnalytics } from "../../../../hooks/useAnalytics";
import { trackClick } from "../../../../utils/analyticsUtils";

const ImageBlock = ({
  section,
  setPopupState,
  handleClose,
  popupData,
  index,
  productIndex,
  productData,
  parentData,
}) => {
  const { getCouponCode, addCart, upgradeOrder, checkout } = useFormSumbmit({
    popupData,
    setPopupState,
    index,
    productIndex,
    productData,
    handleClose,
  });
  const { isMobile } = useApp();
  const { actionHandler } = useAction();
  const {
    trigger,
    reset,
    formState: { errors },
  } = useFormContext();
  const { addAnalyticsEvent } = useAnalytics();

  const popupType = useMemo(() => popupData?.popUpType, [popupData]);
  const popupId = useMemo(() => popupData?._id, [popupData]);

  const sectionImages = useMemo(() => {
    if (parentData) {
      if (section?.link_field) {
        const parsedValue = getValueFromParentData(
          section?.link_field,
          parentData
        );
        if (parsedValue) {
          return [parsedValue];
        }
      }
    }
    return section.src.images;
  }, [parentData, section?.src?.images]);

  const fileURL = useMemo(() => {
    const fileSrc = pickRandomValueFromArray(sectionImages);

    if (fileSrc instanceof File) {
      return URL.createObjectURL(fileSrc);
    } else {
      return fileSrc;
    }
  }, [sectionImages]);
  const disabled = false;
  const { type: actionType, parameters: actionParameters } =
    section?.setting?.action || {};

  const handleOnClick = async (e) => {
    let formData = {};

    const form = e.target.closest("form");

    if (form) {
      const tempFormData = new FormData(form);
      formData = Object.fromEntries(tempFormData?.entries());
    }

    if (disabled) return;

    if (actionType && actionType !== "close_popup") {
      void trackClick({
        popupId: popupData?._id,
        popupType: popupData?.popUpType || popupData?.template_id?.title,
        campaignId: popupData?.campaign_Id,
        clickTarget: "image",
        actionType,
        productId:
          productData?.id ??
          productData?.admin_graphql_api_id ??
          parentData?.id ??
          parentData?.admin_graphql_api_id,
        addAnalyticsEvent,
      });
    }

    switch (actionType) {
      case "go_to_step":
        const { openStep } = actionParameters;
        setPopupState((prev) => ({
          ...prev,
          [`${popupType}_${popupId}`]: {
            ...prev[`${popupType}_${popupId}`],
            activeStep: openStep,
            openPopup: true,
          },
        }));
        break;
      case "open_link":
        const { link } = actionParameters;
        const { new_tab, close_after } = section?.setting?.action ?? {};

        if (link) {
          if (new_tab) {
            window.open(link, "_blank");
          } else {
            window.location.href = link;
          }
        }
        if (close_after) {
          handleClose(e);
        }
        break;
      case "form_submit":
        const { event, openStep: step } = actionParameters;
        const isValid = await trigger();
        if (isValid) {
          switch (event) {
            case "get_coupon_code":
              await getCouponCode(step);
              break;
            case "product_add_cart":
              await addCart(formData);
              break;
            case "product_upgrade_order":
              await upgradeOrder(formData);
              break;
            case "product_check_out":
              await checkout(formData, false, {
                buyNowAction: actionParameters?.buyNowAction,
                clickEvent: e,
              });
              break;
            default:
              break;
          }
          reset();
        }
        break;
      case "close_popup":
        handleClose(e);
        reset();
        break;
      default:
        break;
    }

    await manageAnalytics();
  };

  const manageAnalytics = async () => {
    if (section?.setting?.isConversationEnabled) {
      const userPopupConversion = JSON.parse(
        localStorage.getItem("user-popup-conversion") || "[]"
      );

      if (userPopupConversion.includes(popupData._id)) {
        await addAnalyticsEvent({
          eventType: analyticsEventTypes.IMPRESSION,
          popupType: popupData.template_id?.title,
          popupId: popupData._id,
          ...(popupData?.campaign_Id && { campaignId: popupData?.campaign_Id }),
        });
      }

      addAnalyticsEvent({
        eventType: analyticsEventTypes.CONVERSION,
        popupType: popupData.template_id?.title,
        popupId: popupData._id,
        ...(popupData?.campaign_Id && { campaignId: popupData?.campaign_Id }),
      });

      localStorage.setItem(
        "user-popup-conversion",
        JSON.stringify([...userPopupConversion, popupData._id])
      );
    }
  };

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCss?.[isMobile ? "mobile" : "desktop"]
    ),
    objectFit:
      section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"]?.objectFit ||
      "cover",
  };

  return (
    <img
      onClick={(e) => actionHandler(() => handleOnClick(e))}
      src={fileURL}
      alt="Image"
      style={blockCSS}
      draggable={false}
      fetchPriority="high"
    />
  );
};

export default ImageBlock;
